import React, { Component } from 'react';
import TwoColumnTable from '../TwoColumnTable';
import ContentOutline from '../ContentOutline';
import TextBlock from '../TextBlock';

import sharedStyles from '../sharedStyles.scss';

const EXAM_SUMMARY = [
  {
    topic: 'Seeks Business for the Broker-Dealer from Customers and Potential Customers',
    description:
      'The basics of customer-facing sales activities related to a general securities representative role. What you need to know and do when describing investment products and services to customers with the intent of obtaining their business.',
    weight: '9 questions, 7%',
  },
  {
    topic: 'Regulation of Investment Advisers',
    description:
      'The definition and regulation of an Investment Adviser, including state-registered and federal covered advisers. Contains topics including activities requiring registration.',
    weight: '3 questions, 5%',
  },
  {
    topic: 'Regulation of IARs',
    description:
      'The definition and regulation of Investment Adviser Representatives (IARs). Contains topics including activities requiring IAR registration.',
    weight: '3 questions, 5%',
  },
  {
    topic: 'Regulation of Broker-Dealers',
    description:
      'The definition and regulation of Broker-Dealers. Contains topics including registration and post-registration requirements, bookkeeping requirements, and Broker-Dealer Agent supervision.',
    weight: '9 questions, 15%',
  },
  {
    topic: 'Regulation of Agents',
    description:
      'The definition and regulation of Agents of Broker-Dealers. Contains topics including registration and post-registration requirements and bookkeeping requirements.',
    weight: '9 questions, 15%',
  },
  {
    topic: 'Regulation of Securities and Issuers',
    description:
      'The definition and regulation of Securities and Issuers. Contains topics such as state registration and post-registration requirements, exemptions, and state anti fraud authority.',
    weight: '3 questions, 5%',
  },
  {
    topic: 'Regulation of Administrative Provisions',
    description:
      'The authority of state securities administrators, types of administrative actions, and other penalties and liabilities.',
    weight: '6 questions, 10%',
  },
  {
    topic: 'Communications with Customers and Prospects',
    description:
      'Required disclosures, what happens if you unlawfully represent your registration, and rules around performance guarantees. Also covers customer agreements requirements for activities such as new accounts, margin trading, options trading.',
    weight: '12 questions, 20%',
  },
  {
    topic: 'Ethical Practices and Obligations',
    description:
      'Ethical practices and obligations related to compensation, customer funds and securities, conflicts of interest, criminal activities, cyber security, data protection, and other ethical issues.',
    weight: '15 questions, 25%',
  },
];

class Overview extends Component {
  render() {
    const { currentUser, product, inflatedContent } = this.props;

    return (
      <div className={sharedStyles.layout}>
        <div className={sharedStyles.title}>
          Uniform Securities Agent State Law Exam{' '}
          <span className={sharedStyles.shortTitle}>(FINRA Series 63)</span>
        </div>
        <div className={sharedStyles.explanation}>
          <div className={sharedStyles.blurb}>
            The Series 63 exam - the Uniform Securities Agent State Law Examination -
            assesses one&apos;s knowledge of the state laws associated with securities and
            registered representative activities.
          </div>
          <div className={sharedStyles.facts}>
            <ul>
              <li>
                <em>Time</em> 75 minutes
              </li>
              <li>
                <em>Format</em> 65 multiple choice
              </li>
              <li>
                <em>Passing score</em> 72% (44/60)
              </li>
              <li>
                <em>Exam fee</em> $147
              </li>
              <li>
                <em>Details</em>{' '}
                <a
                  href="https://www.nasaa.org/exams/exam-content-outlines/series-63-exam-content-outline/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  NASAA <i className="fad fa-external-link" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        <TextBlock title="Overview">
          <p>
            The Uniform Securities Act (or &quot;USA&quot;) is a model legislation adopted
            by states, meaning it&apos;s a framework of laws and regulations that states
            can adopt and implement to properly regulate the securities industry. Nearly
            all states require individuals to pass state-based exams like the Series 63 as
            a condition of registration.
          </p>

          <p>
            The exam contains a total of 65 questions, 5 of which are experimental and
            unscored.
          </p>

          <p>
            Despite commonly being called the &quot;FINRA Series 63&quot; it is more
            accurately a North American Securities Administrators Association (NASAA) exam
            administered by FINRA.
          </p>
        </TextBlock>

        <TwoColumnTable title="NASAA's exam summary" records={EXAM_SUMMARY} />

        <ContentOutline
          currentUser={currentUser}
          product={product}
          inflatedContent={inflatedContent}
        />

        <TextBlock title="Eligibility and sponsorship">
          <p>
            Financial professionals typically take the Series 63 as their final exam after
            completing their SIE Exam and Series 6, 7, or 79 license. However, you can
            take the Series 63 as your first FINRA exam. Unlike most FINRA exams, you do
            not need a sponsor to take the Series 63.
          </p>
        </TextBlock>

        <TextBlock title="Administration of the exam">
          <p>
            The Series 63 exam is administered via computer. A tutorial on how to take the
            exam is provided prior to taking the exam.
          </p>

          <p>
            Each candidate&apos;s exam includes 5 additional, unidentified pretest items
            that do not contribute toward the candidate&apos;s score. The pretest items
            are randomly distributed throughout the exam. Therefore, each candidate&apos;s
            exam consists of a total of 65 items (60 scored and 5 unscored).
          </p>

          <p>
            There is no penalty for guessing. Therefore, candidates should attempt to
            answer all items.
          </p>

          <p>Candidates will be allowed 75 minutes to complete the Series 63 exam.</p>

          <p>
            Candidates are not permitted to bring reference materials to their testing
            session. Severe penalties are imposed on candidates who cheat or attempt to
            cheat on FINRA-administered exams.
          </p>
        </TextBlock>

        <TextBlock title="Scoring of the exam">
          <p>
            All candidate test scores are placed on a common scale using a statistical
            adjustment process known as equating. Equating scores to a common scale
            accounts for the slight variations in difficulty that may exist among the
            different sets of exam items that candidates receive. This allows for a fair
            comparison of scores and ensures that every candidate is held to the same
            passing standard regardless of which set of exam items they received.
          </p>
        </TextBlock>
      </div>
    );
  }
}

export default Overview;
